// /login/mfa-verify — final TOTP gate. POST /v1/auth/mfa/verify, on success
// store access token + me, then push to /terminal.

import { ErrorBanner, FormField, LoginShell } from '@/components/LoginShell';
import { ApiError, authFetch } from '@/lib/api';
import { useOnceMountGuard } from '@/lib/auth-guards';
import { type MeProfile, useAuth } from '@/state/auth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

export const Route = createFileRoute('/login/mfa-verify')({
  component: MfaVerifyPage,
});

function MfaVerifyPage() {
  const flowSession = useAuth((s) => s.flowSession);
  const setAccess = useAuth((s) => s.setAccess);
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useOnceMountGuard(() => useAuth.getState().flowSession != null, { to: '/login' });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!flowSession) return;
    setErr(null);
    setBusy(true);
    try {
      const body: { flow_session: string; totp_code?: string; recovery_code?: string } = {
        flow_session: flowSession,
      };
      if (useRecovery) body.recovery_code = recovery.trim().toUpperCase();
      else body.totp_code = code;
      const data = await authFetch<{
        access_token: string;
        expires_in: number;
        me: MeProfile;
      }>('/v1/auth/mfa/verify', { method: 'POST', body });
      setAccess(data.access_token, data.me);
      void navigate({ to: '/terminal' });
    } catch (e) {
      setErr(e instanceof ApiError ? e.code : 'internal_error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoginShell
      step="STEP 4 / 4 · VERIFY"
      title="Enter your 6-digit code"
      sub="Open your authenticator app and enter the current code for Eidollon. Lost your device? Use a recovery code instead."
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ErrorBanner code={err} />
        {!useRecovery ? (
          <FormField label="6-DIGIT CODE" mono>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoComplete="one-time-code"
              required
              style={{ flex: 1, fontFamily: 'var(--mono)', letterSpacing: 4, fontSize: 14 }}
            />
          </FormField>
        ) : (
          <FormField label="RECOVERY CODE" mono hint="Format: ABCD-EFGH-JKMN">
            <input
              type="text"
              value={recovery}
              onChange={(e) => setRecovery(e.target.value.toUpperCase().slice(0, 14))}
              autoComplete="off"
              required
              style={{ flex: 1, fontFamily: 'var(--mono)', letterSpacing: 2 }}
            />
          </FormField>
        )}
        <button
          type="submit"
          className="btn pri"
          disabled={busy || (useRecovery ? recovery.length < 12 : code.length !== 6)}
          style={{ height: 32, justifyContent: 'center' }}
        >
          {busy ? 'VERIFYING…' : 'ENTER TERMINAL'}
        </button>
        <button
          type="button"
          className="btn gh"
          onClick={() => {
            setUseRecovery((v) => !v);
            setErr(null);
          }}
          style={{ height: 28, justifyContent: 'center' }}
        >
          {useRecovery ? 'USE TOTP CODE INSTEAD' : 'USE A RECOVERY CODE'}
        </button>
      </form>
    </LoginShell>
  );
}
